<div class="page-head">
  <div>
    <h1>Maintenance</h1>
    <div class="sub">
      Close the desktop app, the mobile app or one business while you work on it. Merchants see a maintenance page
      instead of the app; you keep working here. Nothing lifts on its own: the expected end is only what the page says.
    </div>
  </div>
  <div class="head-actions">
    <button type="button" class="act-ghost" [disabled]="loading" (click)="load()">Refresh</button>
  </div>
</div>

<div class="page-body">
  @if (error) {
    <div class="note note-err">{{ error }}</div>
  }
  @if (notice) {
    <div class="note note-ok">{{ notice }}</div>
  }

  @if (loading && !channels[0].notice && businesses.length === 0 && !unavailable) {
    <span class="muted">Loading the maintenance state…</span>
  }

  @if (unavailable) {
    <span class="muted">{{ unavailable }}</span>
  } @else {
    <!-- Platform-wide: one panel per app. -->
    <div class="panel-title">Whole platform</div>
    @for (row of channels; track row.key) {
      <div class="panel mt-panel" [attr.data-channel]="row.key">
        <div class="mt-row">
          <div class="mt-text">
            <div class="mt-title">
              <strong>{{ row.name }}</strong>
              <span class="pill" [class.pill-green]="!row.notice" [class.pill-red]="row.notice">
                {{ row.notice ? 'Under maintenance' : 'Open' }}
              </span>
            </div>
            <div class="muted">{{ row.covers }}</div>
            @if (row.notice; as n) {
              <div class="mt-facts">
                <div>Since {{ utc(n.startedAtUtc) | date:'d MMM y, HH:mm' }} by {{ n.startedBy || '—' }}</div>
                @if (n.expectedEndUtc) { <div>Expected back {{ utc(n.expectedEndUtc) | date:'d MMM y, HH:mm' }}</div> }
                <div class="mt-message">“{{ n.message || defaultMessage }}”</div>
              </div>
            }
          </div>
          @if (!isForming(channelTarget(row)) && !isLifting(channelTarget(row))) {
            <div class="mt-actions">
              @if (row.notice) {
                <button type="button" class="act-ghost act-sm" [disabled]="saving" (click)="openChannelForm(row)">Change words…</button>
                <button type="button" class="act act-sm" [disabled]="saving" (click)="askLiftChannel(row)">Lift maintenance…</button>
              } @else {
                <button type="button" class="act-danger act-sm" [disabled]="saving" (click)="openChannelForm(row)">Put under maintenance…</button>
              }
            </div>
          }
        </div>

        @if (isForming(channelTarget(row))) {
          <ng-container *ngTemplateOutlet="formTpl"></ng-container>
        }
        @if (isLifting(channelTarget(row))) {
          <div class="note note-warn mt-confirm">
            Lift maintenance on the <strong>{{ row.name | lowercase }}</strong> for every business? Tills go straight back in.
            <div class="confirm-actions">
              <button type="button" class="act-ghost act-sm" [disabled]="saving" (click)="cancelLift()">Keep closed</button>
              <button type="button" class="act act-sm" [disabled]="saving" (click)="confirmLift()">{{ saving ? 'Saving…' : 'Yes, lift it' }}</button>
            </div>
          </div>
        }
      </div>
    }

    <!-- One business at a time. -->
    <div class="panel-title mt-section">Businesses under maintenance</div>
    <div class="panel">
      @if (businesses.length === 0) {
        <div class="muted">No business is under maintenance.</div>
      } @else {
        <div class="tbl-wrap">
          <table class="tbl">
            <thead>
              <tr><th>Business</th><th>Since</th><th>By</th><th>Expected back</th><th>Message</th><th></th></tr>
            </thead>
            <tbody>
              @for (b of businesses; track b.businessId) {
                <tr [attr.data-business]="b.businessId">
                  <td>
                    <a [routerLink]="['/businesses', b.businessId]">{{ b.businessName || b.businessId }}</a>
                    @if (b.businessName) { <div class="muted small">{{ b.businessId }}</div> }
                  </td>
                  <td>{{ utc(b.startedAtUtc) | date:'d MMM y, HH:mm' }}</td>
                  <td>{{ b.startedBy || '—' }}</td>
                  <td>{{ b.expectedEndUtc ? (utc(b.expectedEndUtc) | date:'d MMM y, HH:mm') : '—' }}</td>
                  <td class="mt-cell-message">{{ b.message || defaultMessage }}</td>
                  <td class="mt-cell-actions">
                    @if (!isForming(businessTarget(b)) && !isLifting(businessTarget(b))) {
                      <button type="button" class="act-ghost act-sm" [disabled]="saving" (click)="openBusinessForm(b)">Change words…</button>
                      <button type="button" class="act act-sm" [disabled]="saving" (click)="askLiftBusiness(b)">Lift…</button>
                    }
                  </td>
                </tr>
                @if (isForming(businessTarget(b))) {
                  <tr><td colspan="6"><ng-container *ngTemplateOutlet="formTpl"></ng-container></td></tr>
                }
                @if (isLifting(businessTarget(b))) {
                  <tr><td colspan="6">
                    <div class="note note-warn mt-confirm">
                      Lift maintenance for <strong>{{ b.businessName || b.businessId }}</strong>? Its tills and phones go straight back in.
                      <div class="confirm-actions">
                        <button type="button" class="act-ghost act-sm" [disabled]="saving" (click)="cancelLift()">Keep closed</button>
                        <button type="button" class="act act-sm" [disabled]="saving" (click)="confirmLift()">{{ saving ? 'Saving…' : 'Yes, lift it' }}</button>
                      </div>
                    </div>
                  </td></tr>
                }
              }
            </tbody>
          </table>
        </div>
      }

      <!-- Closing a business from here; the business profile's Access panel does the same for one business. -->
      <div class="mt-pick">
        <label class="field">
          <span>Put a business under maintenance</span>
          <input class="inp" type="text" list="mt-businesses" [(ngModel)]="businessQuery" (focus)="loadBusinessesForPicker()"
            placeholder="Business name or id" [disabled]="saving" />
          <datalist id="mt-businesses">
            @for (b of allBusinesses; track b.businessId) { <option [value]="b.name">{{ b.businessId }}</option> }
          </datalist>
        </label>
        @if (!form || form.kind !== 'business' || businesses.length === 0) {
          <button type="button" class="act-danger act-sm" [disabled]="saving || !businessQuery.trim()" (click)="openPickedBusinessForm()">
            Put under maintenance…
          </button>
        }
      </div>
      @if (form && form.kind === 'business' && !rewording) {
        <ng-container *ngTemplateOutlet="formTpl"></ng-container>
      }
    </div>

    <p class="muted note-line">Every change is written to the admin activity log. Platform staff are never shown the maintenance page, so you can check the app yourself while it is closed.</p>
  }
</div>

<!-- The one form, for a channel or a business, closing or rewording. -->
<ng-template #formTpl>
  <div class="note note-warn mt-form">
    @if (rewording) {
      Change what <strong>{{ targetName(form) }}</strong> shows while it is under maintenance.
    } @else if (form?.kind === 'channel') {
      Put the <strong>{{ targetName(form) | lowercase }}</strong> under maintenance for <strong>every business</strong>?
      {{ (form?.kind === 'channel' && form && form.kind === 'channel') ? (channels[0].key === form.channel ? channels[0].whenClosed : channels[1].whenClosed) : '' }}
    } @else {
      Put <strong>{{ targetName(form) }}</strong> under maintenance? Its tills, phones and website orders stop until you lift it; other businesses are not affected.
    }
    <div class="form-grid mt-fields">
      <label class="field mt-field-wide">
        <span>What merchants read (blank uses the standard wording)</span>
        <textarea class="inp" rows="3" [(ngModel)]="message" [maxlength]="messageMax" [placeholder]="defaultMessage" [disabled]="saving"></textarea>
        <span class="muted small">{{ message.length }}/{{ messageMax }}</span>
      </label>
      <label class="field">
        <span>Expected back (optional, your local time)</span>
        <input class="inp" type="datetime-local" [(ngModel)]="expectedEndLocal" [disabled]="saving" />
        @if (expectedEndError) { <span class="form-error">{{ expectedEndError }}</span> }
      </label>
    </div>
    <div class="confirm-actions">
      <button type="button" class="act-ghost act-sm" [disabled]="saving" (click)="cancelForm()">Cancel</button>
      <button type="button" [class]="(rewording ? 'act' : 'act-danger') + ' act-sm confirm-btn'" [disabled]="saving || !!expectedEndError" (click)="confirmForm()">
        {{ saving ? 'Saving…' : rewording ? 'Save words' : (form?.kind === 'channel' ? 'Yes, put it under maintenance for every business' : 'Yes, put this business under maintenance') }}
      </button>
    </div>
  </div>
</ng-template>
